/*
 * <aurora-bg> — BRANU 流体オーロラ背景 Web Component
 *
 * 多段domain warp（fbmで歪ませた座標をさらにfbmで歪ませる）のWebGLシェーダーで、
 * Figmaプラグイン級のマーブル質感をページ背景として描画する。
 *
 * 使い方（bodyの先頭に1タグ。コンテンツ側は position:relative + z-index:1 以上にする）:
 *   <script src="aurora-bg.js"></script>
 *   <aurora-bg></aurora-bg>
 *
 * 属性:
 *   preset="branu"           名前付きプリセット。AuroraBg.definePreset() で追加登録できる
 *   theme="auto|light|dark"  auto(既定)は html.dark を監視して追従。html.onsite で自動非表示
 *   seed="13"                模様の乱数。ページごとに変える（全ページ同一＝テンプレ感の残り香）
 *   speed="0.35"             流れの速さ。0で静止
 *   warp="1.35"              歪み強度（プラグインの Strength 相当）
 *   scale="1"                模様の大きさ（小さいほど雄大）
 *   saturation="1"           彩度。0でモノクロ、1超で強調（目安0〜1.6）
 *   fade="0"                 オーロラの淡さ＝不透明度の逆（0=オーロラ全開, 0.9=ほぼ下地）。
 *                            fadeを上げてもオーロラが薄まるだけで、下地の低周波の陰影は残る
 *                            ※前景のガラス面が背景の強い色で濃く見える時に上げる
 *   grain="0.1"              粒状ノイズの強さ。0でオフ（目安0〜0.3）
 *   veil="on|off"            光条＋グレイン層（既定on。タイトル域の可読性を守る）
 *   colors="#4070ff,#7a6eff,…"  色点（最大5個・#rrggbb）。指定した個数だけ先頭から上書き
 *   bg="#081026"             下地色
 *   colors-light / colors-dark / bg-light / bg-dark  テーマ別指定（colors/bgより優先）
 * 属性はプリセット値を個別に上書きする（プリセット < 属性）。
 *
 * プリセット登録（playgroundの「Copy Preset」が出力する形式）:
 *   AuroraBg.definePreset('my-page', {
 *     extends: 'branu',                     // 省略時も branu がベース
 *     params: { warp:1.6, grain:.18, seed:42 },
 *     light: { bg:'#dfeafd' }, dark: {}     // パレット上書き（任意）
 *   });
 *   <aurora-bg preset="my-page"></aurora-bg>
 *
 * 挙動:
 *   - prefers-reduced-motion: reduce では1フレームだけ描画して静止
 *   - タブ非表示中は描画停止
 *   - WebGL不可/コンテキスト喪失時はCSSグラデにフォールバック
 */
(function(){
  'use strict';

  var FALLBACK = {
    light: 'linear-gradient(112deg, rgba(255,255,255,.7) 0%, rgba(255,255,255,.24) 20%, transparent 42%), linear-gradient(150deg,#cddffc,#e0ecfe 38%,#f0f6ff 68%,#d6e4fb)',
    dark:  'linear-gradient(112deg, rgba(148,175,255,.11) 0%, transparent 36%), linear-gradient(150deg,#081026,#0b1330 55%,#071022)'
  };

  var GRAIN = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/></svg>\")";

  var VEIL = {
    light: GRAIN + ', linear-gradient(112deg, rgba(255,255,255,.70) 0%, rgba(255,255,255,.26) 20%, transparent 44%)',
    dark:  GRAIN + ', linear-gradient(112deg, rgba(148,175,255,.14) 0%, transparent 38%)'
  };

  /* 色点はDSトークン系（青主役＋青紫〜ピンクの偏光縁、シアン不使用）
     ダークは色点を端へ・半径を絞る（明度の両極：紺の面を残す） */
  var PRESETS = {
    branu: {
      light: {
        bg: '#d6e0ff',      // 下地グラデ 起点(bgangle方向の手前=左下)
        bg2: '#e0f7ff',     // 下地グラデ 終点(右上)
        cols: ['#7d9dff','#a99bff','#e2d4f4','#9282ff','#6f93ff'],  // アクセントはライラック(旧#ffdbeb)
        pts: [[ .95, .55, .95,1],[ 1.15,-.25, .75,1],[ .35, .95, .45,1],[-1.05,-.75, .85,1],[-.25,-1.05, .65,1]],
        dim: 0.2
      },
      dark: {
        bg: '#081026',
        bg2: '#0c1836',     // 下地グラデ 終点(ダークは僅かに明るい紺)
        cols: ['#4070ff','#7a6eff','#c9a2e6','#6250f7','#2c5dff'],  // アクセントは柔らかいオーキッド(旧#ff8cc3)
        pts: [[ 1.15, .68, .62,1],[ 1.30,-.35, .48,1],[ .40, 1.05, .34,1],[-1.20,-.85, .55,1],[-.35,-1.15, .42,1]],
        dim: 0.6
      },
      params: { warp:1.05, speed:0.69, scale:0.81, saturation:1, fade:0.74, grain:0.05, seed:5, bgangle:62 }
    }
  };

  var VS = 'attribute vec2 a; void main(){ gl_Position = vec4(a,0.,1.); }';

  var FS = [
    'precision highp float;',
    'uniform vec2  u_res;',
    'uniform float u_time;',
    'uniform float u_warp;',
    'uniform float u_seed;',
    'uniform float u_dim;',   // 中央減光。ドリフトで色が中央へ流れてもコントラストを守る
    'uniform float u_sat;',   // 彩度（1=そのまま、0=モノクロ）
    'uniform float u_fade;',  // オーロラの淡さ＝不透明度の逆（陰影付き下地へ寄せる。下地の陰影は常設）
    'uniform float u_grain;', // 粒状ノイズの強さ
    'uniform float u_zoom;',  // 模様スケール
    'uniform vec3  u_bg;',
    'uniform vec3  u_bg2;',   // 下地グラデの2色目（未指定時は u_bg と同値＝単色）
    'uniform float u_bgang;', // 下地グラデの角度(度)。0=左→右, 90=下→上
    'uniform vec3  u_col[5];',
    'uniform vec4  u_pt[5];',
    'float hash(vec2 p){',
    '  p = fract(p*vec2(234.34, 435.345) + u_seed*7.13);',
    '  p += dot(p, p+34.23);',
    '  return fract(p.x*p.y);',
    '}',
    'float noise(vec2 p){',
    '  vec2 i = floor(p), f = fract(p);',
    '  vec2 u = f*f*(3.-2.*f);',
    '  return mix(mix(hash(i), hash(i+vec2(1,0)), u.x),',
    '             mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), u.x), u.y);',
    '}',
    /* オクターブ4・減衰.45：高周波を出すと「煙化」する */
    'float fbm(vec2 p){',
    '  float v = 0., a = .55;',
    '  mat2 rot = mat2(.8, .6, -.6, .8);',
    '  for(int i=0;i<4;i++){',
    '    v += a*noise(p);',
    '    p = rot*p*1.9 + vec2(3.7, 1.9);',
    '    a *= .45;',
    '  }',
    '  return v;',
    '}',
    /* グレイン専用hash（hash12）。fbm用hashは画素座標が大きいと織物状の相関が出る */
    'float ghash(vec2 p){',
    '  vec3 p3 = fract(vec3(p.xyx) * .1031);',
    '  p3 += dot(p3, p3.yzx + 33.33);',
    '  return fract((p3.x + p3.y) * p3.z);',
    '}',
    'vec3 lin(vec3 c){ return pow(c, vec3(2.2)); }',
    'void main(){',
    '  vec2 uv = gl_FragCoord.xy / u_res;',
    '  float aspect = u_res.x / u_res.y;',
    '  vec2 p = (uv - .5) * vec2(aspect, 1.) * 1.7 * u_zoom;',
    '  float t = u_time * .05;',
    /* 多段domain warp：ここがリッチさの核 */
    '  vec2 q = vec2(fbm(p + vec2(0., 0.)  + t*vec2(.9,  .62)),',
    '                fbm(p + vec2(5.2,1.3) - t*vec2(.55, .80)));',
    '  vec2 r = vec2(fbm(p + 2.4*q + vec2(1.7, 9.2) + t*vec2(.28,.36)),',
    '                fbm(p + 2.4*q + vec2(8.3, 2.8) - t*vec2(.32,.22)));',
    '  vec2 w = p + u_warp * 1.5 * (r - .5);',
    '  vec3 col = lin(u_bg);',
    '  for(int i=0;i<5;i++){',
    '    if(u_pt[i].w < .5) continue;',
    '    float d = length(w - u_pt[i].xy);',
    '    float wgt = exp(-pow(d / max(u_pt[i].z, .05), 2.0) * 2.6);',
    '    wgt *= .68 + .62 * fbm(w*.85 + float(i)*7.31);',   // マーブル濃淡。上げると絵の具化
    '    wgt *= mix(1., clamp(length(p)*.75, .15, 1.), u_dim);',
    '    col = mix(col, lin(u_col[i]), clamp(wgt, 0., 1.));',
    '  }',
    '  float f = fbm(p*1.1 + 2.6*r);',
    '  col += lin(u_col[0]) * f * f * .06;',
    '  col = pow(max(col, 0.), vec3(1./2.2));',
    /* 彩度：ガンマ後（知覚空間）で輝度を軸に伸縮 */
    '  float lum = dot(col, vec3(.299,.587,.114));',
    '  col = mix(vec3(lum), col, u_sat);',
    /* 背景の陰影：fadeに依存しない常設の低周波濃淡（下地に奥行きを与える。オーロラを消しても残る） */
    '  float ca = cos(radians(u_bgang)), sa = sin(radians(u_bgang));',
    '  float g = clamp(dot(uv - .5, vec2(ca, sa)) + .5 + (fbm(p*.4) - .5)*.05, 0., 1.);',
    '  vec3 shadedBg = mix(u_bg, u_bg2, g);',
    /* 淡さ＝オーロラの不透明度：オーロラを「陰影付き下地」の上に重ねる（fade高でも下地の陰影は残る） */
    '  col = mix(col, shadedBg, clamp(u_fade, 0., 1.));',
    /* 粒状ノイズ：静的なフィルムグレイン。ディザ（バンディング防止）を兼ねる */
    '  col += (ghash(gl_FragCoord.xy) - .5) * max(u_grain, .006);',
    '  gl_FragColor = vec4(col, 1.);',
    '}'
  ].join('\n');

  var TPL =
    '<style>' +
    ':host{position:fixed;inset:0;z-index:0;pointer-events:none;display:block}' +
    'canvas{position:absolute;inset:0;width:100%;height:100%;display:block}' +
    '.veil{position:absolute;inset:0}' +
    '</style>' +
    '<canvas></canvas><div class="veil"></div>';

  function hex(h){
    return [1,3,5].map(function(i){ return parseInt(h.slice(i,i+2),16)/255; });
  }

  function num(el, name, def){
    var v = parseFloat(el.getAttribute(name));
    return isNaN(v) ? def : v;
  }

  var HEX_RE = /^#[0-9a-fA-F]{6}$/;

  /* "#4070ff, #7a6eff" → ['#4070ff','#7a6eff']（不正値は捨てる） */
  function parseColors(str){
    if(!str) return null;
    var list = str.split(',').map(function(s){ return s.trim(); }).filter(function(s){ return HEX_RE.test(s); });
    return list.length ? list : null;
  }

  class AuroraBg extends HTMLElement {
    static get observedAttributes(){ return ['preset','theme','seed','speed','warp','scale','saturation','fade','grain','veil','colors','bg','bg2','bgangle','colors-light','colors-dark','bg-light','bg-dark','bg2-light','bg2-dark']; }

    connectedCallback(){
      if(this._init) return;
      this._init = true;
      this._t = 0; this._raf = null; this._lost = false;

      var sh = this.attachShadow({mode:'open'});
      sh.innerHTML = TPL;
      this._canvas = sh.querySelector('canvas');
      this._veilEl = sh.querySelector('.veil');

      this._still = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._setupGL();

      var self = this;
      this._mo = new MutationObserver(function(){ self._applyTheme(); });
      this._mo.observe(document.documentElement, {attributes:true, attributeFilter:['class']});
      this._onResize = function(){ self._resize(); self._drawOnce(); };
      addEventListener('resize', this._onResize);
      this._onVis = function(){ document.hidden ? self._stop() : self._start(); };
      document.addEventListener('visibilitychange', this._onVis);

      this._applyTheme();
      this._resize();
      this._start();
    }

    disconnectedCallback(){
      this._stop();
      if(this._mo) this._mo.disconnect();
      removeEventListener('resize', this._onResize);
      document.removeEventListener('visibilitychange', this._onVis);
    }

    attributeChangedCallback(){
      if(!this._init) return;
      this._stop();          // speed等の変更を確実に反映（_applyThemeが再始動する）
      this._applyTheme();
    }

    /* ---- WebGL ---- */
    _setupGL(){
      var gl = this._canvas.getContext('webgl', {antialias:false, alpha:false});
      this._gl = gl;
      if(!gl){ this._fallback(); return; }

      var self = this;
      this._canvas.addEventListener('webglcontextlost', function(e){
        e.preventDefault(); self._lost = true; self._stop(); self._fallback();
      });

      function compile(type, src){
        var s = gl.createShader(type);
        gl.shaderSource(s, src); gl.compileShader(s);
        if(!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
        return s;
      }
      var prog = gl.createProgram();
      gl.attachShader(prog, compile(gl.VERTEX_SHADER, VS));
      gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(prog);
      if(!gl.getProgramParameter(prog, gl.LINK_STATUS)){ this._gl = null; this._fallback(); return; }
      gl.useProgram(prog);

      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 3,-1, -1,3]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(prog, 'a');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

      this._u = {};
      var names = ['u_res','u_time','u_warp','u_seed','u_dim','u_sat','u_fade','u_grain','u_zoom','u_bg','u_bg2','u_bgang','u_col','u_pt'];
      for(var i=0;i<names.length;i++) this._u[names[i]] = gl.getUniformLocation(prog, names[i]);
    }

    _fallback(){
      var mode = this._resolveTheme();
      this.style.background = FALLBACK[mode];
      if(this._canvas) this._canvas.style.display = 'none';
    }

    _resolveTheme(){
      var attr = this.getAttribute('theme');
      if(attr === 'light' || attr === 'dark') return attr;
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    }

    _applyTheme(){
      /* 現場モード：流体層は出さない（a11y。ソリッド面の正はページ側CSS） */
      var onsite = document.documentElement.classList.contains('onsite');
      this.style.display = onsite ? 'none' : '';
      if(onsite){ this._stop(); return; }

      var mode = this._resolveTheme();
      this._veilEl.style.backgroundImage = this.getAttribute('veil') === 'off' ? 'none' : VEIL[mode];

      /* プリセット解決：プリセット値を属性が個別上書き */
      var PR = PRESETS[this.getAttribute('preset')] || PRESETS.branu;
      this._pr = PR;

      if(!this._gl || this._lost){ this._fallback(); return; }
      var gl = this._gl, P = PR[mode], prm = PR.params;

      /* 色の属性上書き：colors-{mode} > colors > プリセット。指定した個数だけ先頭から差し替え */
      var cols = P.cols, bg = P.bg;
      var ca = parseColors(this.getAttribute('colors-' + mode)) || parseColors(this.getAttribute('colors'));
      if(ca) cols = P.cols.map(function(c, i){ return ca[i] || c; });
      var ba = this.getAttribute('bg-' + mode) || this.getAttribute('bg');
      if(ba && HEX_RE.test(ba.trim())) bg = ba.trim();

      /* 下地グラデ2色目：bg2-{mode} > bg2 > プリセットP.bg2 > bg（未指定なら単色） */
      var bg2 = this.getAttribute('bg2-' + mode) || this.getAttribute('bg2') || P.bg2 || bg;
      bg2 = (bg2 && HEX_RE.test(String(bg2).trim())) ? String(bg2).trim() : bg;

      gl.uniform3fv(this._u.u_bg, hex(bg));
      gl.uniform3fv(this._u.u_bg2, hex(bg2));
      gl.uniform1f(this._u.u_bgang, num(this, 'bgangle', prm.bgangle != null ? prm.bgangle : 135));
      gl.uniform3fv(this._u.u_col, cols.reduce(function(a,c){ return a.concat(hex(c)); }, []));
      gl.uniform4fv(this._u.u_pt, P.pts.reduce(function(a,p){ return a.concat(p); }, []));
      gl.uniform1f(this._u.u_warp, num(this, 'warp', prm.warp));
      gl.uniform1f(this._u.u_seed, num(this, 'seed', prm.seed));
      gl.uniform1f(this._u.u_dim, P.dim);
      gl.uniform1f(this._u.u_sat, num(this, 'saturation', prm.saturation));
      gl.uniform1f(this._u.u_fade, num(this, 'fade', prm.fade || 0));
      gl.uniform1f(this._u.u_grain, num(this, 'grain', prm.grain));
      gl.uniform1f(this._u.u_zoom, num(this, 'scale', prm.scale));
      if(!this._raf) this._start();
    }

    _resize(){
      if(!this._gl || this._lost) return;
      var dpr = Math.min(devicePixelRatio || 1, 2);
      this._canvas.width = innerWidth * dpr;
      this._canvas.height = innerHeight * dpr;
      this._gl.viewport(0, 0, this._canvas.width, this._canvas.height);
      this._gl.uniform2f(this._u.u_res, this._canvas.width, this._canvas.height);
    }

    _drawOnce(){
      if(!this._gl || this._lost) return;
      this._gl.uniform1f(this._u.u_time, this._t);
      this._gl.drawArrays(this._gl.TRIANGLES, 0, 3);
    }

    _start(){
      if(!this._gl || this._lost || this._raf) return;
      if(document.documentElement.classList.contains('onsite')) return;
      var speed = num(this, 'speed', (this._pr || PRESETS.branu).params.speed);
      if(this._still || speed === 0){ this._drawOnce(); return; }  // 静止：1フレームのみ

      var self = this, last = performance.now();
      this._raf = requestAnimationFrame(function frame(now){
        self._t += (now - last)/1000 * speed; last = now;
        self._drawOnce();
        self._raf = requestAnimationFrame(frame);
      });
    }

    _stop(){
      if(this._raf){ cancelAnimationFrame(this._raf); this._raf = null; }
    }
  }

  /* プリセット登録API：extendsのベース（省略時branu）へ浅いマージ */
  AuroraBg.definePreset = function(name, def){
    var base = PRESETS[def.extends || 'branu'] || PRESETS.branu;
    PRESETS[name] = {
      light:  Object.assign({}, base.light,  def.light),
      dark:   Object.assign({}, base.dark,   def.dark),
      params: Object.assign({}, base.params, def.params)
    };
    /* 既にそのプリセットを参照している要素へ反映 */
    document.querySelectorAll('aurora-bg[preset="' + name + '"]').forEach(function(el){
      if(el._init){ el._stop(); el._applyTheme(); }
    });
  };
  AuroraBg.presets = PRESETS;

  customElements.define('aurora-bg', AuroraBg);
  window.AuroraBg = AuroraBg;
})();
